import React, { useMemo, useRef, useState } from 'react';
import { ILaborCallout } from '../../../../entities-generated/common/labor-callout';
import { IStandardMaterial } from '../../../../entities-generated/common/standard-material';
import { IStandardLabor } from '../../../../entities-generated/common/standard-labor';
import { any, firstOrDefault, remove, where } from '../../../../framework/utils/collection';
import { EventNotificationService, useEventNotification } from '../../../../framework/services/event-notification';
import { ProjectEvents } from '../../events';
import * as formService from '../../form-services/assess-record-labor';
import { IAssessmentLaborEditingModel, AssessmentLaborEditingModel } from '../../entities/assess-rec-labor-fm';
import { IMaterialCallout, MaterialCallout } from '../../../../entities-generated/common/material-callout';
import { BwjButtonType, BwjButton } from '../../../../framework/components/bwj-button';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { BwjLabelField } from '../../../../framework/components/bwj-label-field';
import { MaterialListing } from '../material-listing';
import { getNowUnixTimestamp } from '../../../../framework/utils/date-time';
import { MaterialStatus } from '../../../../entities-generated/common/material-status';

interface ILaborEditorPanelProps {
  onLaborUpdated: (labor: ILaborCallout) => void;
}

const subscriberId = 'Panel.LaborEditor';

const getRelevantMaterials = (materials: IStandardMaterial[], labor: IStandardLabor | null) => {
  if(!labor) { return materials; } // all materials are relevant for custom labor

  const matches = (mt: IStandardMaterial, ids: string[]) => any(ids, i => i === mt.id);
  return where(materials, m => matches(m, labor.standardMaterials));
}

export const LaborEditorPanel: React.FunctionComponent<ILaborEditorPanelProps> = (props) => {
  const [showPanel, setShowPanel] = useState(false);
  const blockPanelDismiss = useRef(false);
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, false);
  const currentArgs = useRef<IAssessmentLaborEditingModel>(new AssessmentLaborEditingModel());
  const customMaterials = useMemo(() => where(formModel.materials, m => !!m.standardMaterialId === false), [formModel]);
  const standardMaterials = (() => {
    const em = currentArgs.current;
    return getRelevantMaterials(em.standardMaterialOptions, em.standardLabor);
  })();
  
  // we have to do this to defeat a bug where, when two panels are on display at the same time,
  // underlying panels are dismissed with any click on the screen.  The blockPanelDismiss flag
  // stops the panel from dismissing even when it wants to.  When this panel is free to be dismissable again
  // (because the top-most panel is gone), the event below is broadcast and consumed
  useEventNotification(() => {
    blockPanelDismiss.current = false;
  }, [], ProjectEvents.LaborEditorPanelFreeToClose, subscriberId);

  useEventNotification((args: IAssessmentLaborEditingModel) => {
    currentArgs.current = args;
    formService.initializeService(args);
    setShowPanel(true);
  }, [], ProjectEvents.LaborEditorPanel_Launch, subscriberId);

  const onEditMaterial = (material: IMaterialCallout) => {
    blockPanelDismiss.current = true;
    EventNotificationService.dispatch(ProjectEvents.MaterialEditorPanel_Launch, material);
  };

  const onAddCustomMaterial = () => {
    const material = new MaterialCallout();
    material.id = getNowUnixTimestamp().toString();

    onEditMaterial(material);
  };

  const onAddStandardMaterial = (std: IStandardMaterial) => {
    const material: IMaterialCallout = {
      standardMaterialId: std.id,
      id: getNowUnixTimestamp().toString(),
      description: std.description,
      vendorUrl: std.vendorUrl,
      pricePerUnit: std.price,
      quantity: 0,
      procurementStatus: MaterialStatus.Unacquired,
      expectedDeliveryDate: 0
    };

    onEditMaterial(material);
  };

  const onRemoveMaterial = (material: IMaterialCallout) => {
    const materials = remove(formModel.materials, m => m.id === material.id);
    formService.materials.onUpdate(materials);
  };

  const onClosePanel = () => {
    if(blockPanelDismiss.current) { return; }

    EventNotificationService.dispatch(ProjectEvents.AssessmentEditorPanelFreeToClose);
    setShowPanel(false);
  }

  const onSaveChanges = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid) {
        const labor = formService.toLaborCallout();
        props.onLaborUpdated(labor);
        onClosePanel();
      }
    });
  }

  return (
    <BwjSidePanel
      showPanel={showPanel}
      title={'Edit Work Item'}
      footerButtons={[{
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: 'Save changes',
        disabled: formModel.isStandardLabor === false && hasUnsavedChanges === false,
        onClick: onSaveChanges
      }, {
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Cancel',
        onClick: onClosePanel
      }]}
      onDismiss={onClosePanel}
    >
      <BwjGrid>
        {formModel.isStandardLabor === false ? <>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.description.id}
              required={true}
              label={formService.description.description}
              onUpdate={formService.description.onUpdate}
              input={formModel.description}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.CustomerSummary.id}
              required={false}
              label={formService.CustomerSummary.description}
              onUpdate={formService.CustomerSummary.onUpdate}
              input={formModel.CustomerSummary}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={6}>
            <BwjTextField
              id={formService.time.id}
              required={true}
              label={formService.time.description}
              onUpdate={formService.time.onUpdate}
              input={formModel.time}
              filter={/^\d*$/}
            />
          </Col>
        </Row>
        </>
        :
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjLabelField
              id={formService.description.id}
              label={formService.description.description}
              value={formModel.description.value}
            />
          </Col>
        </Row>}
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <h2>Work Materials</h2>
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            {standardMaterials.map(stdMaterial =>
              <MaterialListing
                key={stdMaterial.id}
                standardMaterial={stdMaterial}
                material={firstOrDefault(formModel.materials, m => m.standardMaterialId === stdMaterial.id)}
                onAddMaterial={onAddStandardMaterial}
                onEditMaterial={onEditMaterial}
                onRemoveMaterial={onRemoveMaterial}
              />
            )}
            {customMaterials.map(material =>
              <MaterialListing
                key={material.id}
                standardMaterial={undefined}
                material={material}
                onAddMaterial={onAddStandardMaterial}
                onEditMaterial={onEditMaterial}
                onRemoveMaterial={onRemoveMaterial}
              />
            )}
          </Col>
        </Row>
        {(!currentArgs.current.standardLabor || currentArgs.current.standardLabor.allowCustomMaterials) &&
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <BwjButton
                buttonStyle={BwjButtonType.PrimaryStandard}
                text={'Add custom material'}
                onClick={onAddCustomMaterial}
              />
            </div>
          </Col>
        </Row>}
      </BwjGrid>
    </BwjSidePanel>
  );
};
